import React, {useCallback, useState, useEffect, useMemo} from 'react';

import useGetUser, {userInitialData, UserType} from 'js/hooks/useGetUser/useGetUser';
import MixPanelWrapper from 'js/servises/MixPanelWrapper';
import {useCookies} from 'react-cookie';

interface CommonContextInterface {
	apiPath: string,
	oneLoginBaseURL: string,
	currentUser: UserType,
	isCustomerLogged: boolean | null,
	activeMenuItemId: number,
	setActiveMenuItemId: Function,
	showGDPR: 'show' | 'toggle' | 'hide' | 'none',
	setShowGDPR: Function,
	isAnnouncementBar: boolean,
	setIsAnnouncementBar: Function
}

export const CommonContext = React.createContext<CommonContextInterface>({
		apiPath: '',
		oneLoginBaseURL: '',
		currentUser: userInitialData,
		isCustomerLogged: null,
		activeMenuItemId: null,
		setActiveMenuItemId: value => value,
		showGDPR: 'hide',
		setShowGDPR: value => value,
		isAnnouncementBar: false,
		setIsAnnouncementBar: value => value
	}
);

const CommonContextProvider = (props) => {
	const {
		NODE_ENV,
		REACT_APP_LOCAL_LINK,
		REACT_APP_PROD_LINK,
		REACT_APP_ONE_LOGIN_PROD_BASE_URL,
		REACT_APP_ONE_LOGIN_DEV_BASE_URL,
		REACT_APP_MIX_PANEL_INIT_COOKIE,
		REACT_APP_ACCEPTED_POLICY_COOKIE
	} = process.env || {};

	const [cookies, setCookie] = useCookies([]);
	const [activeMenuItemId, setActiveMenuItemId] = useState<number>(null);
	const [isAnnouncementBar, setIsAnnouncementBar] = useState<boolean>(false);
	const [showGDPR, setShowGDPR] = useState<'show' | 'toggle' | 'hide' | 'none'>('show');

	const getApiPath = useCallback(():string => {
		if (NODE_ENV === 'development') {
			return REACT_APP_LOCAL_LINK;
		}
		return window.location.origin;
	}, [NODE_ENV, REACT_APP_LOCAL_LINK]);

	const getOneLoginBaseURL = useCallback(() => {
		if (window.location.origin === REACT_APP_PROD_LINK) {
			return REACT_APP_ONE_LOGIN_PROD_BASE_URL;
		}
		return REACT_APP_ONE_LOGIN_DEV_BASE_URL;
	}, [
		REACT_APP_PROD_LINK,
		REACT_APP_ONE_LOGIN_PROD_BASE_URL,
		REACT_APP_ONE_LOGIN_DEV_BASE_URL
	]);

	const currentUserData = useGetUser(getApiPath());
	const {currentUser, isCustomerLogged}  = useMemo(() => currentUserData || {
		currentUser: null,
		isCustomerLogged: false
	}, [currentUserData]);

	const cookieOption = useMemo(() => (
		{
			path: '/',
			domain: `.${location.hostname}`,
			maxAge: 315360000
		}
	), []);

	useEffect(() => {
		const cookie = cookies[REACT_APP_ACCEPTED_POLICY_COOKIE];
		if (!isCustomerLogged || cookie) {
			return;
		}
		setCookie(REACT_APP_MIX_PANEL_INIT_COOKIE, true, {...cookieOption});
	}, [
		REACT_APP_ACCEPTED_POLICY_COOKIE,
		REACT_APP_MIX_PANEL_INIT_COOKIE,
		cookieOption,
		cookies, isCustomerLogged, setCookie]);

	useEffect(() => {
		(async () =>
			MixPanelWrapper.Init(currentUser, isCustomerLogged, window?.location?.origin !== REACT_APP_PROD_LINK)
		)();
	}, [currentUser, isCustomerLogged, REACT_APP_PROD_LINK]);

	return (
		<CommonContext.Provider
			value={
				{
					showGDPR,
					setShowGDPR,
					activeMenuItemId,
					isCustomerLogged,
					isAnnouncementBar,
					setActiveMenuItemId,
					setIsAnnouncementBar,
					apiPath: getApiPath(),
					oneLoginBaseURL: getOneLoginBaseURL(),
					currentUser: currentUserData?.currentUser
				}
			}>
			{props.children}
		</CommonContext.Provider>
	);
};

export default CommonContextProvider;
